import React, { useState, useCallback } from "react";
import { Box, Info } from "lucide-react";
import { ProcessorNodeData } from "../../types/pipeline";

interface VectorConfigHelperProps {
  onConfigGenerated: (config: any) => void;
  disabled?: boolean;
  selectedNode: { id: string; data: ProcessorNodeData } | null;
  updateNode: (
    id: string,
    updates: Partial<{ data: ProcessorNodeData }>
  ) => void;
}

/**
 * VectorConfigHelper - UI for configuring the Vector Processor (SWRM-176)
 *
 * Provides a user-friendly interface for:
 * - Setting the vector analysis task description
 * - Viewing information about vector inputs
 * - Displaying common use cases and help
 */
export const VectorConfigHelper: React.FC<VectorConfigHelperProps> = ({
  onConfigGenerated,
  disabled,
  selectedNode,
}) => {
  // Initialize from node config
  const [embeddingModel, setEmbeddingModel] = useState(
    selectedNode?.data.config?.embedding_model || "text-embedding-3-small"
  );

  const [timeout, setTimeoutValue] = useState(
    selectedNode?.data.config?.timeout || 3600
  );

  // Generate config from current state
  const generateConfig = useCallback(() => {
    const config = {
      embedding_model: embeddingModel,
      timeout,
    };
    onConfigGenerated(config);
  }, [embeddingModel, timeout, onConfigGenerated]);

  // Apply changes
  const handleApply = useCallback(() => {
    generateConfig();
  }, [generateConfig]);

  return (
    <div className="border border-gray-300 dark:border-gray-600 rounded-md p-4 mb-4 bg-gray-50 dark:bg-gray-800/50">
      <div className="flex items-center gap-2 mb-3">
        <Box className="w-4 h-4 text-purple-600 dark:text-purple-400" />
        <h3 className="text-sm font-medium text-gray-900 dark:text-white">
          Vector Processor Configuration
        </h3>
      </div>

      {/* Task Input - Primary Required Input */}
      <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-md">
        <div className="flex items-start gap-2">
          <Info className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-blue-800 dark:text-blue-300">
            <p className="font-medium mb-1">Required Input: 'task'</p>
            <p>
              Configure the 'task' input in the Properties panel below.
              Describe what you want the vector processor to do (e.g., "Create a
              hierarchical taxonomy from the research findings").
            </p>
          </div>
        </div>
      </div>

      {/* Settings */}
      <div className="space-y-3 mb-4">
        {/* Embedding Model */}
        <div className="flex flex-col gap-1">
          <label className="text-sm text-gray-700 dark:text-gray-300">
            Embedding Model:
          </label>
          <select
            value={embeddingModel}
            onChange={(e) => {
              setEmbeddingModel(e.target.value);
              // Generate config immediately
              const config = {
                embedding_model: e.target.value,
                timeout,
              };
              onConfigGenerated(config);
            }}
            disabled={disabled}
            className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded
              bg-white dark:bg-gray-700 text-gray-900 dark:text-white
              focus:outline-none focus:ring-2 focus:ring-purple-500
              disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <option value="text-embedding-3-small">
              text-embedding-3-small (1536 dim, fast, recommended)
            </option>
            <option value="text-embedding-3-large">
              text-embedding-3-large (3072 dim, higher quality)
            </option>
          </select>
        </div>

        {/* Timeout */}
        <div className="flex flex-col gap-1">
          <label className="text-sm text-gray-700 dark:text-gray-300">
            Timeout (seconds):
          </label>
          <input
            type="number"
            value={timeout}
            onChange={(e) => setTimeoutValue(Number(e.target.value))}
            onBlur={handleApply}
            disabled={disabled}
            min={60}
            max={7200}
            className="w-32 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded
              bg-white dark:bg-gray-700 text-gray-900 dark:text-white
              focus:outline-none focus:ring-2 focus:ring-purple-500
              disabled:opacity-50 disabled:cursor-not-allowed"
          />
        </div>
      </div>

      {/* Input Types Info */}
      <div className="mb-4 p-3 bg-gray-100 dark:bg-gray-700/50 rounded-md">
        <h4 className="text-xs font-medium text-gray-900 dark:text-white mb-2">
          💡 Input Types
        </h4>
        <div className="text-xs text-gray-700 dark:text-gray-300 space-y-1">
          <p>
            <strong>Vector inputs (type="vector"):</strong> Pre-computed vectors
            from a previous run, auto-loaded on initialization
          </p>
          <p>
            <strong>Data inputs (CSV/JSON):</strong> Raw data that will be
            vectorized using the add_to_vector_db tool
          </p>
        </div>
      </div>

      {/* Use Cases */}
      <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-md">
        <h4 className="text-xs font-medium text-green-800 dark:text-green-300 mb-2">
          🎯 Common Use Cases
        </h4>
        <ul className="text-xs text-green-700 dark:text-green-400 space-y-1">
          <li>• <strong>Hierarchical taxonomy generation</strong> - Bottom-up affinity clustering</li>
          <li>• <strong>Duplicate detection</strong> - Find similar or duplicate items</li>
          <li>• <strong>Semantic search</strong> - Find items related to a topic</li>
          <li>• <strong>Content gap analysis</strong> - Identify underrepresented themes</li>
        </ul>
      </div>

      {/* Help text */}
      <div className="mt-3 text-xs text-gray-500 dark:text-gray-400 space-y-1">
        <p><strong>Example Task:</strong></p>
        <p className="pl-2">
          "Create a 3-level hierarchical taxonomy from the UX research findings.
          Use bottom-up clustering with 10 leaf clusters per 100 items.
          Label each cluster with a 2-3 word descriptive name.
          Save the result as a Mermaid mindmap to /taxonomy/findings-v1.md"
        </p>
      </div>
    </div>
  );
};

